/**
 * CrewAvatars — the "who you're with" row.
 *
 * A compact run of the familiar Avatar circles (lead first), optionally followed
 * by first names with the lead marked. Used on the crew Today and My Week
 * screens so a crew member can see who else is on each task at a glance.
 */
import { Avatar } from './Avatar';
import { cn } from '@/lib/cn';

export interface CrewFace {
  display_name: string;
  crew_role: string;
  avatar_url: string | null;
  is_lead: boolean;
}

function namesLine(crew: CrewFace[]): string {
  return crew
    .map((c) => {
      const first = c.display_name.split(' ')[0] || c.display_name;
      return c.is_lead ? `${first} (lead)` : first;
    })
    .join(', ');
}

export function CrewAvatars({
  crew,
  max = 5,
  withNames = false,
  className,
  avatarClass,
  nameClass,
}: {
  crew: CrewFace[];
  max?: number;
  withNames?: boolean;
  className?: string;
  avatarClass?: string;
  nameClass?: string;
}) {
  if (!crew || crew.length === 0) return null;
  const shown = crew.slice(0, max);
  const extra = crew.length - shown.length;
  return (
    <div className={cn('flex items-center gap-2 min-w-0', className)}>
      <div className="flex items-center gap-1 shrink-0">
        {shown.map((c, i) => (
          <Avatar
            key={i}
            name={c.display_name}
            src={c.avatar_url}
            className={cn('w-6 h-6 text-[9px]', avatarClass)}
          />
        ))}
        {extra > 0 && (
          <span
            className={cn(
              'inline-flex items-center justify-center rounded-full bg-fill border border-line w-6 h-6 text-[9px] font-bold text-ink-dim',
              avatarClass,
            )}
          >
            +{extra}
          </span>
        )}
      </div>
      {withNames && <span className={cn('text-xs truncate', nameClass)}>{namesLine(crew)}</span>}
    </div>
  );
}
